import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import ImageUploader from "@/components/ImageUploader";
import RichEditor from "@/components/RichEditor";
import {
  Newspaper, Plus, Edit2, Trash2, Loader2, X, Eye, EyeOff, Archive, RotateCcw,
} from "lucide-react";

const EMPTY = {
  title_ar: "", title_en: "", body_ar: "", body_en: "",
  cover_image: "", cover_image_path: null, published: true,
};

export default function AdminNews() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get("/admin/news").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const visible = useMemo(() => rows.filter((r) => showArchived ? true : !r.is_archived), [rows, showArchived]);

  const save = async () => {
    if (!editing.title_en || !editing.title_ar) { toast.error("Titles required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/news/${editing.id}`, editing);
      else await api.post("/admin/news", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const togglePublish = async (n) => {
    try { await api.put(`/admin/news/${n.id}`, { ...n, published: !n.published }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (n) => { if (window.confirm(`Archive "${n.title_en}"?`)) { await api.post(`/admin/news/${n.id}/archive`); toast.success("Archived"); load(); } };
  const restore = async (n) => { await api.post(`/admin/news/${n.id}/restore`); toast.success("Restored"); load(); };
  const remove = async (n) => {
    if (!window.confirm("Permanently delete? Prefer archive.")) return;
    try {
      await api.delete(`/admin/news/${n.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-news">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Content</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3"><Newspaper size={26} className="text-gold" /> News</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-navy/70 inline-flex items-center gap-2">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="news-show-archived" />
            Show archived
          </label>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-gold" data-testid="news-add"><Plus size={14} /> New</button>
        </div>
      </div>
      <span className="divider-gold" />

      <div className="card-elev divide-y divide-navy/10">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-navy/50">No news items.</div>
        ) : visible.map((n) => (
          <div key={n.id} className="p-5 flex items-center gap-4" data-testid={`news-row-${n.id}`}>
            {n.cover_image ? (
              <img src={n.cover_image} alt="" className="w-16 h-16 object-cover rounded-sm border border-navy/10" />
            ) : (
              <div className="w-16 h-16 bg-cream border border-navy/10 rounded-sm flex items-center justify-center text-navy/30 text-[10px]">no image</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-navy font-medium">{n.title_en}</div>
              <div className="text-xs text-navy/60">{n.title_ar} · {new Date(n.created_at).toLocaleDateString()}</div>
            </div>
            <div className="flex items-center gap-1 whitespace-nowrap">
              {n.is_archived ? <Badge className="bg-navy/20 text-navy/70"><Archive size={11} className="inline me-1" />Archived</Badge>
               : n.published ? <Badge className="bg-emerald-100 text-emerald-800"><Eye size={11} className="inline me-1" />Live</Badge>
               : <Badge className="bg-amber-100 text-amber-800"><EyeOff size={11} className="inline me-1" />Draft</Badge>}
              {!n.is_archived && <button onClick={() => togglePublish(n)} className="text-navy/70 p-1.5" data-testid={`news-toggle-${n.id}`}>{n.published ? <EyeOff size={14} /> : <Eye size={14} />}</button>}
              <button onClick={() => setEditing({ ...EMPTY, ...n })} className="text-navy/70 p-1.5" data-testid={`news-edit-${n.id}`}><Edit2 size={14} /></button>
              {n.is_archived ? <button onClick={() => restore(n)} className="text-emerald-700 p-1.5" data-testid={`news-restore-${n.id}`}><RotateCcw size={14} /></button>
                             : <button onClick={() => archive(n)} className="text-amber-700 p-1.5" data-testid={`news-archive-${n.id}`}><Archive size={14} /></button>}
              <button onClick={() => remove(n)} className="text-red-600/80 p-1.5" data-testid={`news-del-${n.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-4xl mx-auto rounded-md border border-gold/30 p-8 my-6" onClick={(e) => e.stopPropagation()} data-testid="news-editor">
            <div className="flex justify-between">
              <h2 className="text-2xl text-navy">{editing.id ? "Edit news" : "New news"}</h2>
              <button onClick={() => setEditing(null)} className="text-navy/50"><X size={18} /></button>
            </div>
            <span className="divider-gold" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="field-box" placeholder="Title (AR)" value={editing.title_ar} onChange={(e) => setEditing({ ...editing, title_ar: e.target.value })} data-testid="news-title-ar" />
              <input className="field-box" placeholder="Title (EN)" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} data-testid="news-title-en" />
              <div className="md:col-span-2">
                <label className="text-xs text-navy/70 mb-2 block">Cover image</label>
                <ImageUploader value={editing.cover_image || ""} folder="news" testId="news-cover"
                  onChange={(url, path) => setEditing({ ...editing, cover_image: url || "", cover_image_path: path })} />
              </div>
              <RichEditor testId="news-body-ar" dir="rtl" value={editing.body_ar} onChange={(html) => setEditing({ ...editing, body_ar: html })} />
              <RichEditor testId="news-body-en" dir="ltr" value={editing.body_en} onChange={(html) => setEditing({ ...editing, body_en: html })} />
              <label className="flex items-center gap-2 text-navy md:col-span-2">
                <input type="checkbox" className="w-5 h-5 accent-gold" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="news-publish" />
                Publish immediately
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} className="btn-gold" data-testid="news-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
              </button>
              <button onClick={() => setEditing(null)} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
